import CloseButton from 'components/CloseButton';
import LightboxLayout from 'layout/LightboxLayout';
import { GetStaticPropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import LightBoxItem from 'partials/LightBoxItem';
import { getWhoIsWearing } from 'services/whoIsWearing';
import { WhoIsWearingWithBundle } from 'types';
import { withPageStaticProps } from 'utils/header';
import { slugifySnake } from 'utils/utils';

interface Props {
  post: WhoIsWearingWithBundle;
}

type UrlParams = {
  name: string;
};

const getPageTitle = (celebrity: WhoIsWearingWithBundle) =>
  celebrity.seo?.title ?? `${celebrity.name}, ${celebrity.description}, Wearing Generation Tux`;

export const getStaticPaths = async () => {
  const { allWhoIsWearing: posts } = await getWhoIsWearing();

  const paths = posts.map((post) => {
    if (!post.name) {
      throw new Error(`Encountered a post under Who's Wearing GenTux missing data: ${post._id}`);
    }

    return {
      params: {
        name: slugifySnake(post.name),
      },
    };
  });

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps = withPageStaticProps(async ({ params }: GetStaticPropsContext<UrlParams>) => {
  const { allWhoIsWearing: posts } = await getWhoIsWearing();

  const post = posts.find((post) => {
    const nameAsSlug = slugifySnake(post.name ?? 'missing');

    return nameAsSlug === params?.name;
  });

  if (!post) {
    throw new Error(`Attempted to build a page for a name that doesn't seem to exist: ${params?.name}`);
  }

  return {
    props: {
      post,
    },
  };
});

export const CelebrityWearingGenTux = (props: Props) => {
  const router = useRouter();
  const pageTitle = getPageTitle(props.post);
  const nameAsSlug = slugifySnake(props.post.name!);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>

        <meta name="description" key="description" content={props.post.seo?.description ?? pageTitle} />

        <meta
          property="og:url"
          key="og:url"
          content={`https://generationtux.com/whos-wearing-generation-tux/celebrity/${nameAsSlug}`}
        />
        <meta property="og:type" key="og:type" content="website" />
        <meta property="og:title" key="og:title" content={props.post.opengraph?.title ?? pageTitle} />
        <meta property="og:description" key="og:description" content={props.post.opengraph?.description ?? pageTitle} />
        <meta property="og:image" key="og:image" content={props.post.image?.asset?.url ?? ''} />

        <meta name="twitter:card" key="twitter:card" content="summary_large_image" />
        <meta name="twitter:domain" key="twitter:domain" content="generationtux.com" />
        <meta
          name="twitter:url"
          key="twitter:url"
          content={`https://generationtux.com/whos-wearing-generation-tux/celebrity/${nameAsSlug}`}
        />
        <meta name="twitter:title" key="twitter:title" content={props.post.opengraph?.title ?? pageTitle} />
        <meta
          name="twitter:description"
          key="twitter:description"
          content={props.post.opengraph?.description ?? pageTitle}
        />
        <meta name="twitter:image" key="twitter:image" content={props.post.image?.asset?.url ?? ''} />
      </Head>

      <div className="container !p-0">
        <div className="relative animate-scaleIn shadow-3">
          <CloseButton onClose={() => router.push(`/whos-wearing-generation-tux`)} className="absolute right-0" />

          <div className="row">
            <div className="col-span-12 m-0 flex sm:col-span-6">
              <div className="w-full">
                <img
                  className="ml-auto max-h-[55vh] xs:max-h-[none] xs:w-full xs:object-cover"
                  style={{
                    display: 'block',
                    aspectRatio: `${props.post.image.asset.metadata?.dimensions?.width} / ${props.post.image.asset.metadata?.dimensions?.height}`,
                  }}
                  src={`${props.post.image.asset.url}?w=800&h=800&fm=jpg&fit=fillmax`}
                  alt={props.post.bundle.display_name}
                />
              </div>
            </div>
            <div className="col-span-12 m-0 flex flex-col px-32 pt-32 sm:col-span-6 md:px-64 md:pt-64 lg:px-128">
              <div style={{ flexGrow: 1 }}>
                <h1 className="text-h1-display mb-8">{props.post.name}</h1>
                <p className=" mb-32">{props.post.description}</p>

                <div className="pb-32 md:pb-64">
                  <LightBoxItem lightBoxItem={props.post} error={false} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

CelebrityWearingGenTux.layout = LightboxLayout;

export default CelebrityWearingGenTux;
